import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { toast } from "react-toastify";
import { staticAssumptionService } from "../../utils/services/staticAssumptionService";

const StaticAssumptionModal = ({ open, onClose, onSuccess, editData }) => {
  const isEdit = !!editData;

  const initialData = {
    key: "",
    value: "",
  };

  const [formData, setFormData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editData) {
      setFormData({
        key: editData.key || "",
        value: editData.value || "",
      });
    } else {
      setFormData(initialData);
      setErrors({});
    }
  }, [editData, open]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.key || !formData.key.trim()) {
      newErrors.key = "Key is required";
    }

    if (!formData.value && formData.value !== 0 && formData.value !== "") {
      newErrors.value = "Value is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        key: formData.key.trim(),
        value: String(formData.value).trim(),
      };

      if (isEdit) {
        await staticAssumptionService.edit(submitData, editData._id);
        toast.success("Static assumption updated successfully!");
      } else {
        await staticAssumptionService.create(submitData);
        toast.success("Static assumption created successfully!");
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Submit failed:", error);
      toast.error(
        error?.response?.data?.message || "Failed to save static assumption",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initialData);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit Static Assumption" : "Create Static Assumption"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <div>
            <TextField
              fullWidth
              label="Key"
              value={formData.key}
              onChange={(e) => handleInputChange("key", e.target.value)}
              required
              error={!!errors.key}
              placeholder="e.g., MAX_ORDERS, MIN_STOCK_LEVEL"
            />
            {errors.key && <FormHelperText error>{errors.key}</FormHelperText>}
          </div>
          <div>
            <TextField
              fullWidth
              label="Value"
              value={formData.value}
              onChange={(e) => handleInputChange("value", e.target.value)}
              required
              error={!!errors.value}
              multiline
              minRows={2}
              maxRows={4}
              placeholder="e.g., 1000, true, 50.5"
            />
            {errors.value && (
              <FormHelperText error>{errors.value}</FormHelperText>
            )}
          </div>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? (
            <CircularProgress size={20} />
          ) : isEdit ? (
            "Update"
          ) : (
            "Create"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaticAssumptionModal;
